import type { ConversationView, LoggedMessageView, MessageView } from "@vaulted/shared";
import { formatPhone } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { errorMessage, get, post } from "../api";
import { Alert, Badge, Button, Checkbox, Field, Loading, Panel, PanelHeader, SelectField, Tabs, cx, formatDateTime } from "../components/ui";
import { MessageBody, MessageFiles, MessageTabs, MessagesTab } from "./ClientProfile";
import { ChannelChip, DirectionTag, isPair, pairTextAndEmail } from "../components/MessageKind";
import { ClientPicker, NumberActions, PhoneList, SavePhoneBox, useCommCounts } from "./Voicemail";

/** Every client's texts and emails, replies needing an answer, voicemails, and missed calls (managers). */
export function CommunicationPage() {
  const { tab = "conversations" } = useParams();
  const counts = useCommCounts().data;
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="font-display text-3xl">Communication</h1>
        <p className="text-ink-soft">Texts and emails from clients and every call on the clinic's lines in one place. Replies go out from the clinic's number or email.</p>
      </header>
      <Tabs
        items={[
          { to: "/communication", label: `Conversations${counts?.texts || counts?.emails ? ` (${(counts?.texts ?? 0) + (counts?.emails ?? 0)})` : ""}`, end: true },
          { to: "/communication/phone", label: `Phone${counts?.calls ? ` (${counts.calls})` : ""}` },
          { to: "/communication/all", label: "All messages" },
        ]}
      />
      {tab === "conversations" && <Conversations />}
      {(tab === "phone" || tab === "voicemail") && <PhoneList />}
      {tab === "all" && <AllMessages />}
    </div>
  );
}

function Conversations() {
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({ queryKey: ["comm", "conversations", all], queryFn: () => get<ConversationView[]>(`/api/communications/conversations${all ? "?filter=all" : ""}`), refetchInterval: 30_000 });
  const keyOf = (c: ConversationView) => c.client?.id ?? `n:${c.from}`;
  return (
    <Panel>
      <PanelHeader
        title={all ? "All conversations" : "Needs a reply"}
        description={all ? "Everyone who has texted or emailed the clinic in the last 6 months." : "Clients who texted or emailed and haven't had an answer. Replying, or Done, takes them off the list."}
        action={
          <Button variant="quiet" onClick={() => setAll(!all)}>
            {all ? "Show needs a reply" : "Show all"}
          </Button>
        }
      />
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <Alert>{errorMessage(list.error)}</Alert>
      ) : list.data?.length ? (
        <ul className="px-5 pb-3">
          {list.data.map((c) => {
            const key = keyOf(c);
            return (
              <li key={key} className="border-b border-mist py-3 last:border-0">
                <button className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left" onClick={() => setOpen(open === key ? null : key)}>
                  {c.needsReply > 0 && <span className="size-2.5 rounded-full bg-brand" aria-label="Needs a reply" />}
                  <span className="font-semibold">
                    {c.client ? c.client.name : c.from?.includes("@") ? `Unknown email ${c.from}` : c.known ? `${c.known.name || "Not a client"} ${formatPhone(c.from)}` : `Unknown number ${formatPhone(c.from)}`}
                  </span>
                  <ChannelChip channel={c.last.channel} />
                  {c.known?.blocked && <Badge tone="off">Blocked</Badge>}
                  {c.needsReply > 1 && <Badge tone="brand">{c.needsReply}</Badge>}
                  <span className="ml-auto text-sm text-muted">{formatDateTime(c.last.at)}</span>
                  <span className={cx("w-full truncate text-sm", c.needsReply ? "text-ink" : "text-ink-soft")}>
                    {c.last.direction === "out" ? "You: " : ""}
                    {c.last.body}
                  </span>
                </button>
                {open === key && (
                  <div className="mt-3 flex flex-col gap-3">
                    <DoneBar c={c} />
                    {c.client ? <MessagesTab clientId={c.client.id} /> : <UnknownNumber from={c.from!} />}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="px-5 pb-5 text-sm text-muted">{all ? "No texts from clients yet." : "All caught up."}</p>
      )}
    </Panel>
  );
}

function DoneBar({ c }: { c: ConversationView }) {
  const qc = useQueryClient();
  const done = useMutation({
    mutationFn: () => post("/api/communications/conversations/done", { clientId: c.client?.id ?? null, from: c.client ? null : c.from }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["comm"] }),
  });
  return (
    <div className="flex flex-wrap items-center gap-3">
      {c.client && (
        <Link to={`/clients/${c.client.id}`} className="text-sm font-semibold text-brand underline">
          Open {c.client.name}'s profile
        </Link>
      )}
      {c.needsReply > 0 && (
        <Button variant="secondary" busy={done.isPending} onClick={() => done.mutate()}>
          Done (no reply needed)
        </Button>
      )}
      {done.error && <Alert>{errorMessage(done.error)}</Alert>}
    </div>
  );
}

/** Texts (or emails) from a number or address no client has: read them, then say who it is. */
function UnknownNumber({ from }: { from: string }) {
  const qc = useQueryClient();
  const email = from.includes("@");
  const msgs = useQuery({ queryKey: ["comm", "number", from], queryFn: () => get<MessageView[]>(`/api/communications/number?from=${encodeURIComponent(from)}`) });
  const [savePhone, setSavePhone] = useState(true);
  const link = useMutation({ mutationFn: (clientId: string) => post("/api/communications/link", { from, clientId, savePhone }), onSuccess: () => qc.invalidateQueries({ queryKey: ["comm"] }) });
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2">
        {[...(msgs.data ?? [])].reverse().map((m) => (
          <li key={m.id} className="max-w-[85%] self-start rounded-[var(--radius-control)] bg-mist px-3 py-2 text-sm">
            <div className="mb-0.5 text-xs text-muted">{formatDateTime(m.at)}</div>
            {m.subject && <div className="font-semibold">{m.subject}</div>}
            <MessageBody m={m} />
            <MessageFiles m={m} />
          </li>
        ))}
      </ul>
      <p className="text-sm text-ink-soft">To reply, link this {email ? "email address" : "number"} to the client (or add them as a new client first), then answer from their conversation.</p>
      {email ? <Checkbox label="Save it as their email (if they don't have one)" checked={savePhone} onChange={setSavePhone} /> : <SavePhoneBox checked={savePhone} onChange={setSavePhone} />}
      <ClientPicker onPick={(id) => link.mutate(id)} />
      {!email && <NumberActions phone={from} onDone={() => qc.invalidateQueries({ queryKey: ["comm"] })} />}
      {link.error && <Alert>{errorMessage(link.error)}</Alert>}
    </div>
  );
}

const STATUS_LABEL: Record<MessageView["status"], string> = { scheduled: "Scheduled", sent: "Sent", failed: "Failed", skipped: "Skipped", received: "Received", canceled: "Canceled" };

function AllMessages() {
  const [q, setQ] = useState("");
  const [channel, setChannel] = useState("");
  const [direction, setDirection] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const params = new URLSearchParams({ ...(q.trim() ? { q: q.trim() } : {}), ...(channel ? { channel } : {}), ...(direction ? { direction } : {}), ...(status ? { status } : {}), page: String(page) });
  const list = useQuery({ queryKey: ["comm", "log", params.toString()], queryFn: () => get<LoggedMessageView[]>(`/api/communications/messages?${params}`) });
  const reset = (f: () => void) => (f(), setPage(0));
  return (
    <Panel>
      <PanelHeader title="All messages" description="Every text and email sent or received, including automatic confirmations and reminders." />
      <div className="grid gap-3 px-5 pb-3 sm:grid-cols-4">
        <Field label="Search" type="search" placeholder="Client, number, or words" value={q} onChange={(e) => reset(() => setQ(e.target.value))} />
        <SelectField label="Type" value={channel} onChange={(e) => reset(() => setChannel(e.target.value))}>
          <option value="">Texts and emails</option>
          <option value="sms">Texts</option>
          <option value="email">Emails</option>
        </SelectField>
        <SelectField label="Direction" value={direction} onChange={(e) => reset(() => setDirection(e.target.value))}>
          <option value="">Both</option>
          <option value="in">From clients</option>
          <option value="out">To clients</option>
        </SelectField>
        <SelectField label="Status" value={status} onChange={(e) => reset(() => setStatus(e.target.value))}>
          <option value="">Any</option>
          <option value="sent">Sent</option>
          <option value="scheduled">Scheduled</option>
          <option value="failed">Failed</option>
          <option value="received">Received</option>
        </SelectField>
      </div>
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <Alert>{errorMessage(list.error)}</Alert>
      ) : (
        <ul className="flex flex-col gap-3 px-5 pb-3">
          {pairTextAndEmail(list.data ?? [], (a, b) => (a.client?.id ?? a.to) === (b.client?.id ?? b.to)).map((x) => {
            const versions = isPair(x) ? x.pair : [x];
            const [m, other] = [versions[0]!, versions[1]];
            return (
              <li key={m.id}>
                <MessageTabs
                  versions={versions}
                  head={
                    <>
                      <span>{formatDateTime(m.at)}</span>
                      {m.client ? (
                        <Link to={`/clients/${m.client.id}`} className="text-sm font-semibold text-brand underline">
                          {m.client.name}
                        </Link>
                      ) : (
                        <span className="text-sm font-semibold text-ink">{m.to}</span>
                      )}
                      <DirectionTag direction={m.direction} />
                      <span className="font-semibold text-ink-soft">{m.direction === "in" ? "Reply" : (m.label ?? (m.sentBy ? `by ${m.sentBy}` : "Sent"))}</span>
                      <Badge tone={m.status === "failed" ? "wait" : m.status === "scheduled" ? "brand" : "off"}>{STATUS_LABEL[m.status]}</Badge>
                      {other && other.status !== m.status && <Badge tone={other.status === "failed" ? "wait" : "off"}>Email: {STATUS_LABEL[other.status]}</Badge>}
                    </>
                  }
                />
              </li>
            );
          })}
          {!list.data?.length && <p className="pb-2 text-sm text-muted">Nothing matches.</p>}
        </ul>
      )}
      <div className="flex gap-2 px-5 pb-5">
        <Button variant="quiet" disabled={page === 0} onClick={() => setPage(page - 1)}>
          Newer
        </Button>
        <Button variant="quiet" disabled={(list.data?.length ?? 0) < 50} onClick={() => setPage(page + 1)}>
          Older
        </Button>
      </div>
    </Panel>
  );
}
